import type { ReactNode } from "react";
import { formatDate } from "./prosthesis-meta";
import { ProsthesisTeethDisplay } from "./ProsthesisTeethDisplay";
import { rotuloDoMaterialDoServico } from "./rotulo-do-material";
import { origemDasUnidades, valorDoServicoEmTexto } from "./valor-do-pedido";
import type { ProsthesisServiceRow } from "./types";

/**
 * Um serviço do pedido como ficha técnica, do mesmo jeito pra clínica e laboratório: o nome e a
 * situação no topo; embaixo, o odontograma (só a arcada usada, pra caber grande) e os dados em
 * "rótulo: valor", duas colunas. Campo vazio aparece como "Não informado" — esconder tirava
 * informação (material faltando é um dado).
 */
export function CartaoDoServico({
  service,
  status,
  extra,
  acoes,
}: {
  service: ProsthesisServiceRow & { concludedAt?: Date | string | null };
  /** Pílula (clínica) ou seletor (laboratório), à direita do nome. */
  status: ReactNode;
  /** Depois dos dados: alterações do serviço… */
  extra?: ReactNode;
  acoes?: ReactNode;
}) {
  const quantidade = Math.max(service.quantidade ?? 1, 1);
  const entregue = service.status === "CONCLUIDO" && service.concludedAt ? formatDate(service.concludedAt) : null;
  // Material é o que costuma ser longo: ganha a linha inteira e quebra, em vez de cortar.
  const dados: { rotulo: string; valor: string | null; dica?: string | null; largo?: boolean }[] = [
    { rotulo: "Dentes", valor: service.teeth || null },
    { rotulo: "Cor", valor: service.shadeOption?.code ?? null },
    // O número sozinho não diz nada: a dica conta de onde ele saiu (regra do serviço ou ajuste).
    {
      rotulo: "Unidades",
      valor: String(quantidade),
      dica: origemDasUnidades({ teeth: service.teeth, quantidade, abrangencia: service.serviceCategory.abrangencia }),
    },
    // Preço nulo é preço que esta pessoa não vê (ou o laboratório não tem): não vira "Não informado".
    ...(service.priceCents != null ? [{ rotulo: "Valor", valor: valorDoServicoEmTexto(service) }] : []),
    ...(entregue ? [{ rotulo: "Entregue em", valor: entregue }] : []),
    { rotulo: "Material", valor: rotuloDoMaterialDoServico(service), largo: true },
  ];

  return (
    <div className="rounded-lg border border-zinc-200 dark:border-border">
      {/* flex-wrap: no celular a situação desce pra linha de baixo em vez de cortar o nome do serviço. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 border-b border-zinc-100 px-4 py-2.5 dark:border-border">
        <p className="min-w-0 break-words text-sm font-semibold text-zinc-900 dark:text-foreground">{service.serviceCategory.label}</p>
        <div className="shrink-0">{status}</div>
      </div>

      <div className="flex flex-col gap-4 p-4 sm:flex-row">
        {service.teeth && (
          <div className="flex w-full max-w-[150px] shrink-0 items-center justify-center self-center rounded-md sm:self-start bg-zinc-50 p-2 sm:w-[130px] dark:bg-muted/30" aria-hidden>
            <ProsthesisTeethDisplay teeth={service.teeth} largura={114} soArcadaUsada />
          </div>
        )}

        {/* Coluna em flex: as ações descem pro pé do cartão, alinhadas com o fim do odontograma. */}
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
            {dados.map(({ rotulo, valor, dica, largo }) => (
              <div key={rotulo} className={largo ? "flex min-w-0 items-baseline gap-2 sm:col-span-2" : "flex min-w-0 items-baseline gap-2"}>
                <dt className="w-24 shrink-0 text-[12px] text-zinc-500 dark:text-muted-foreground">{rotulo}</dt>
                <dd className={valor ? "min-w-0 break-words text-[13px] font-medium text-zinc-900 dark:text-foreground" : "text-[13px] text-zinc-400 dark:text-muted-foreground/70"}>
                  {valor ?? "Não informado"}
                  {valor && dica && <span className="ml-1.5 font-normal text-zinc-500 dark:text-muted-foreground">· {dica}</span>}
                </dd>
              </div>
            ))}
          </dl>

          {service.notes && (
            <div className="rounded-md bg-zinc-50 px-3 py-2 text-[13px] text-zinc-700 dark:bg-muted/30 dark:text-foreground/80">
              <span className="text-zinc-500 dark:text-muted-foreground">Observações: </span>
              {service.notes}
            </div>
          )}

          {service.status === "CANCELADO" && service.motivoDoCancelamento && (
            <p className="rounded-md border border-red-200 bg-red-50/60 px-3 py-2 text-[13px] text-zinc-700 dark:border-red-900/40 dark:bg-red-950/10 dark:text-foreground/80">
              <span className="font-semibold text-red-700 dark:text-red-400">Motivo do cancelamento: </span>
              {service.motivoDoCancelamento}
            </p>
          )}

          {extra}

          {acoes && <div className="mt-auto flex flex-wrap items-center justify-end gap-2">{acoes}</div>}
        </div>
      </div>
    </div>
  );
}
